import { ServiceSummary } from '@/types/crm';
import { useOrganization } from '@/services/organizations';
import { iconMap, ServiceTabConfig, getDefaultServiceTabs } from '@/data/serviceTabPresets';
import { NovaSpinner } from '@/components/nova';

interface SummaryPanelsProps {
  summaries: Record<string, ServiceSummary>;
}

export const SummaryPanels = ({ summaries }: SummaryPanelsProps) => {
  const { data: organization, isLoading } = useOrganization();

  const tabs: ServiceTabConfig[] = organization?.service_tabs 
    ? (organization.service_tabs as ServiceTabConfig[])
    : getDefaultServiceTabs();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">Summary</h3>
        <div className="flex items-center justify-center py-8">
          <NovaSpinner size={24} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground">Summary</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 stagger-children">
        {tabs.map((tab, index) => {
          const Icon = iconMap[tab.icon] || iconMap.sparkles;
          const data = summaries[tab.id] || { total: 0, accepted: 0, inProgress: 0 };
          
          return (
            <div key={tab.id} className="card-glass p-5 hover:scale-[1.01] transition-transform duration-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-secondary">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <h4 className="font-semibold text-foreground">{tab.label}</h4>
              </div>
              
              {/* 3 stat columns on desktop, 2 on mobile (third wraps to
                  next row). Was `grid-cols-3` without breakpoints which
                  squeezed labels to illegibility at < 480px. */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <p className="text-2xl font-bold text-foreground number-counter">{data.total}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-status-accepted number-counter">{data.accepted}</p>
                  <p className="text-xs text-muted-foreground">Accepted</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-status-in-progress number-counter">{data.inProgress}</p>
                  <p className="text-xs text-muted-foreground">In Progress</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
